/**
 * The market store — the single mutable source of truth the whole terminal
 * reads from.
 *
 * The central design decision: **market data never passes through React
 * state.** BTCUSDT alone can print several hundred trades a second, and with
 * eight symbols streaming trades, depth, klines and tickers we are handling
 * thousands of messages a second. Routing that through setState would queue
 * thousands of renders a second and the app would sit at single-digit fps
 * doing nothing but reconciliation.
 *
 * Instead:
 *   - ingest writes straight into typed arrays and Maps, allocating nothing
 *   - canvas panels read those structures directly from one shared rAF loop
 *   - React is told "something changed" at ~8Hz, and only for text chrome
 *
 * Every mutable container carries a `seq` counter, so a renderer can compare
 * against what it drew last frame and skip entirely when nothing moved. That
 * is what keeps an idle terminal at ~0% CPU instead of burning a full repaint
 * budget sixty times a second.
 */

import { Emitter } from '../lib/emitter';
import { DEMO } from './mode';
import { demoBase, demoCandles } from './demo';
import { TradeRing } from '../lib/ring';
import { inferPricePrecision } from '../lib/num';
import {
  fetchDepthSnapshot,
  fetchKlines,
  fetchKlinesBefore,
  fetchSymbolMeta,
  fetchTickers,
  syncClock,
  klineStream,
  tickerStream,
  tradeStream,
  depthStream,
  INTERVALS,
  type Interval,
  type SymbolMeta,
  type WsDepth,
  type WsKline,
  type WsTicker,
  type WsTrade,
} from './binance';
import { KlineSeries } from './klines';
import { OrderBook } from './orderbook';
import { pool } from './socket';

export interface TickerState {
  last: number;
  prevPrice: number;
  open: number;
  high: number;
  low: number;
  change: number;
  changePct: number;
  volume: number;
  quoteVolume: number;
  bestBid: number;
  bestAsk: number;
  tradeCount: number;
  /** +1 uptick, -1 downtick, 0 unchanged — drives the flash colour. */
  direction: number;
  updatedAt: number;
}

export class SymbolState {
  readonly trades = new TradeRing(4096);
  readonly book: OrderBook;
  readonly klines = new Map<Interval, KlineSeries>();

  meta: SymbolMeta | null = null;
  pricePrecision = 2;
  qtyPrecision = 4;

  readonly ticker: TickerState = {
    last: NaN,
    prevPrice: NaN,
    open: NaN,
    high: NaN,
    low: NaN,
    change: NaN,
    changePct: NaN,
    volume: NaN,
    quoteVolume: NaN,
    bestBid: NaN,
    bestAsk: NaN,
    tradeCount: 0,
    direction: 0,
    updatedAt: 0,
  };

  /** Bumped by ticker/trade updates so the watchlist can skip clean frames. */
  seq = 0;

  constructor(readonly symbol: string) {
    this.book = new OrderBook(symbol);
  }

  series(interval: Interval): KlineSeries {
    let s = this.klines.get(interval);
    if (!s) {
      s = new KlineSeries(this.symbol, interval);
      this.klines.set(interval, s);
    }
    return s;
  }
}

export interface StoreLayout {
  symbols: string[];
  active: string;
  interval: Interval;
}

const LS_KEY = DEMO ? 'terminal.demo.layout.v1' : 'terminal.layout.v1';
const DEFAULT_SYMBOLS = [
  'BTCUSDT',
  'ETHUSDT',
  'SOLUSDT',
  'BNBUSDT',
  'XRPUSDT',
  'DOGEUSDT',
  'ADAUSDT',
  'LINKUSDT',
];

/** How often React is told to re-render text chrome. 8Hz reads as live. */
const UI_TICK_MS = 125;

type Unsub = () => void;

export class MarketStore {
  private states = new Map<string, SymbolState>();
  private subs = new Map<string, Unsub[]>();

  symbols: string[] = [];
  active = '';
  interval: Interval = '1m';

  /** Throttled "text UI should refresh" signal. */
  readonly tick = new Emitter<void>();
  /** Immediate "the shape of things changed" signal — symbols, interval. */
  readonly structure = new Emitter<void>();
  /** Fired with the symbol whose book needs the depth panel to hard-reset. */
  readonly bookReset = new Emitter<string>();

  /** Monotonic counter for useSyncExternalStore snapshots. */
  version = 0;
  structureVersion = 0;

  private tickTimer = 0;
  private tickPending = false;
  private booted = false;
  private pendingSyncs = new Set<string>();

  constructor() {
    const saved = this.load();
    this.symbols = saved.symbols;
    this.active = saved.active;
    this.interval = saved.interval;
  }

  // -- persistence ----------------------------------------------------------

  private load(): StoreLayout {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const p = JSON.parse(raw) as Partial<StoreLayout>;
        const symbols =
          Array.isArray(p.symbols) && p.symbols.length
            ? p.symbols.filter((s) => typeof s === 'string' && /^[A-Z0-9]{3,24}$/.test(s)).slice(0, 24)
            : DEFAULT_SYMBOLS;
        if (!symbols.length) return { symbols: [...DEFAULT_SYMBOLS], active: DEFAULT_SYMBOLS[0], interval: '1m' };
        return {
          symbols,
          active: typeof p.active === 'string' && symbols.includes(p.active) ? p.active : symbols[0],
          interval: INTERVALS.includes(p.interval as Interval) ? p.interval as Interval : '1m',
        };
      }
    } catch {
      /* corrupt storage is not worth crashing over */
    }
    return { symbols: [...DEFAULT_SYMBOLS], active: DEFAULT_SYMBOLS[0], interval: '1m' };
  }

  private persist(): void {
    try {
      localStorage.setItem(
        LS_KEY,
        JSON.stringify({ symbols: this.symbols, active: this.active, interval: this.interval }),
      );
    } catch {
      /* private mode / quota */
    }
  }

  // -- lifecycle ------------------------------------------------------------

  start(): void {
    if (this.booted) return;
    this.booted = true;
    if (DEMO) {
      for (const symbol of this.symbols) this.seedDemo(symbol);
      this.demoTimer = window.setInterval(() => {
        if (document.hidden || this.demoPaused) return;
        for (const symbol of this.symbols) this.advanceDemo(symbol);
      }, 350);
      this.emitStructure();
      return;
    }

    for (const s of this.symbols) this.attach(s);
    this.attachActive(this.active);

    // Establish the exchange clock offset before anything reports a latency
    // or a data age, then re-check hourly to catch drift and sleep/resume.
    void syncClock();
    window.setInterval(() => void syncClock(), 60 * 60 * 1000);

    void this.loadMeta(this.symbols);
    void this.loadTickers(this.symbols);

    // A reconnect means we may have missed depth diffs and kline closes while
    // we were away. Books are the dangerous one — a book with a hole in it
    // renders a spread that never existed — so they are torn down and rebuilt
    // from a fresh snapshot rather than patched.
    pool.reconnected.on(() => {
      for (const [sym, st] of this.states) {
        st.book.invalidate('socket reconnected');
        this.bookReset.emit(sym);
      }
      if (this.active) void this.syncBook(this.active);
      // A reconnect often follows a sleep, which is exactly when the local
      // clock has drifted furthest.
      void syncClock();
      void this.loadTickers(this.symbols);
      void this.refreshActiveKlines();
      this.notify();
    });
  }

  stop(): void {
    clearInterval(this.demoTimer);
    for (const list of this.subs.values()) for (const u of list) u();
    this.subs.clear();
    pool.shutdown();
    if (this.tickTimer) clearTimeout(this.tickTimer);
    this.booted = false;
  }

  get(symbol: string): SymbolState {
    let st = this.states.get(symbol);
    if (!st) {
      st = new SymbolState(symbol);
      this.states.set(symbol, st);
    }
    return st;
  }

  peek(symbol: string): SymbolState | undefined {
    return this.states.get(symbol);
  }

  get activeState(): SymbolState {
    return this.get(this.active);
  }

  get activeSeries(): KlineSeries {
    return this.get(this.active).series(this.interval);
  }

  // -- subscription management ---------------------------------------------

  /** Streams every watchlist symbol carries, focused or not. */
  private attach(symbol: string): void {
    if (DEMO) { this.seedDemo(symbol); return; }
    if (this.subs.has(symbol)) return;
    const st = this.get(symbol);
    const list: Unsub[] = [];

    list.push(
      pool.subscribe(tickerStream(symbol), (d) => this.onTicker(st, d as WsTicker)),
      pool.subscribe(tradeStream(symbol), (d) => this.onTrade(st, d as WsTrade)),
      pool.subscribe(klineStream(symbol, '1m'), (d) => this.onKline(st, d as WsKline)),
    );

    this.subs.set(symbol, list);
    void this.loadKlines(symbol, '1m');
  }

  private detach(symbol: string): void {
    const list = this.subs.get(symbol);
    if (list) for (const u of list) u();
    this.subs.delete(symbol);
    this.detachActive(symbol);
    this.states.delete(symbol);
  }

  private activeSubs: Unsub[] = [];
  private activeAttached = '';

  /** The extra streams only the focused symbol needs: depth, and its interval. */
  private attachActive(symbol: string): void {
    if (DEMO) { this.seedDemo(symbol); return; }
    if (this.activeAttached === symbol + '|' + this.interval) return;
    this.detachActive();

    const st = this.get(symbol);
    this.activeSubs.push(
      pool.subscribe(depthStream(symbol), (d) => this.onDepth(st, d as WsDepth)),
    );
    if (this.interval !== '1m') {
      this.activeSubs.push(
        pool.subscribe(klineStream(symbol, this.interval), (d) => this.onKline(st, d as WsKline)),
      );
    }
    this.activeAttached = symbol + '|' + this.interval;

    void this.loadKlines(symbol, this.interval);
    void this.syncBook(symbol);
  }

  private detachActive(only?: string): void {
    if (only && !this.activeAttached.startsWith(only + '|')) return;
    for (const u of this.activeSubs) u();
    this.activeSubs = [];
    this.activeAttached = '';
  }

  // -- ingest ---------------------------------------------------------------

  private onTicker(st: SymbolState, d: WsTicker): void {
    const t = st.ticker;
    const last = +d.c;
    if (Number.isFinite(t.last) && last !== t.last) {
      t.direction = last > t.last ? 1 : -1;
      t.prevPrice = t.last;
    }
    t.last = last;
    t.open = +d.o;
    t.high = +d.h;
    t.low = +d.l;
    t.change = +d.p;
    t.changePct = +d.P;
    t.volume = +d.v;
    t.quoteVolume = +d.q;
    t.bestBid = +d.b;
    t.bestAsk = +d.a;
    t.tradeCount = d.n;
    t.updatedAt = d.E;
    if (!st.meta) st.pricePrecision = inferPricePrecision(last);
    st.seq++;
    alertHook?.(st.symbol, last);
    this.notify();
  }

  private onTrade(st: SymbolState, d: WsTrade): void {
    const price = +d.p;
    const quantity = +d.q;
    // `m` is "was the buyer the maker" — true means a resting bid was hit, so
    // the aggressor was a seller. Getting this backwards inverts the whole
    // tape and every buy/sell volume split downstream.
    const isBuy = d.m === false;

    st.trades.push(price, quantity, d.T, isBuy ? 1 : 0, d.t);

    const t = st.ticker;
    if (Number.isFinite(t.last) && price !== t.last) {
      t.direction = price > t.last ? 1 : -1;
      t.prevPrice = t.last;
    }
    t.last = price;
    t.updatedAt = d.T;

    // Klines only refresh every second or two; folding the print in keeps the
    // rightmost candle exactly in step with the tape.
    for (const series of st.klines.values()) {
      if (series.loaded) series.applyTrade(price, quantity, d.T, isBuy);
    }

    st.seq++;
    alertHook?.(st.symbol, price);
    this.notify();
  }

  private onDepth(st: SymbolState, d: WsDepth): void {
    const result = st.book.ingest(d);
    if (result === 'gap') {
      this.bookReset.emit(st.symbol);
      void this.syncBook(st.symbol);
    }
  }

  private onKline(st: SymbolState, d: WsKline): void {
    const interval = d.k.i as Interval;
    const series = st.klines.get(interval);
    if (!series || !series.loaded) return;
    series.applyLive(d.k);
  }

  // -- REST loading ---------------------------------------------------------

  private async loadKlines(symbol: string, interval: Interval): Promise<void> {
    const series = this.get(symbol).series(interval);
    if (series.loadingHistory) return;
    if (series.loaded && series.length >= 400) return;
    series.loadingHistory = true;
    try {
      const rows = await fetchKlines(symbol, interval, 600);
      series.replaceAll(rows);
      this.notify();
    } catch (err) {
      console.warn('[store] kline load failed', symbol, interval, err);
    } finally {
      series.loadingHistory = false;
    }
  }

  /** After a reconnect the visible series may have a hole; refetch it. */
  private async refreshActiveKlines(): Promise<void> {
    const series = this.activeSeries;
    if (!series.loaded) { await this.loadKlines(this.active, this.interval); return; }
    try {
      const rows = await fetchKlines(this.active, this.interval, 600);
      series.replaceAll(rows);
      this.notify();
    } catch {
      /* the live stream will heal it eventually */
    }
  }

  /** Load older bars for the chart when the user pans past the left edge. */
  async loadOlder(symbol: string, interval: Interval): Promise<number> {
    const series = this.get(symbol).series(interval);
    if (DEMO) { series.historyExhausted = true; return 0; }
    if (series.loadingHistory || series.historyExhausted || series.length === 0) return 0;
    series.loadingHistory = true;
    try {
      const rows = await fetchKlinesBefore(symbol, interval, series.openTime.get(0), 500);
      const added = series.prepend(rows);
      this.notify();
      return added;
    } catch {
      return 0;
    } finally {
      series.loadingHistory = false;
    }
  }

  /**
   * Full order book resynchronisation.
   *
   * Retries matter here: step 3 of Binance's procedure can legitimately fail
   * when the snapshot comes back older than the diffs already buffered, and
   * the only fix is to ask again a moment later.
   */
  private async syncBook(symbol: string): Promise<void> {
    if (this.pendingSyncs.has(symbol)) return;
    this.pendingSyncs.add(symbol);
    const book = this.get(symbol).book;

    try {
      for (let attempt = 0; attempt < 5; attempt++) {
        book.beginSync();
        try {
          const snap = await fetchDepthSnapshot(symbol, 1000);
          if (book.applySnapshot(snap)) {
            this.notify();
            return;
          }
        } catch (err) {
          console.warn('[store] depth snapshot failed', symbol, err);
        }
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
      book.invalidate('could not obtain a usable snapshot');
    } finally {
      this.pendingSyncs.delete(symbol);
    }
  }

  private async loadMeta(symbols: string[]): Promise<void> {
    if (symbols.length === 0) return;
    try {
      const metas = await fetchSymbolMeta(symbols);
      for (const m of metas) {
        const st = this.get(m.symbol);
        st.meta = m;
        st.pricePrecision = m.pricePrecision;
        st.qtyPrecision = m.qtyPrecision;
      }
      this.notify();
    } catch (err) {
      console.warn('[store] exchangeInfo failed, using inferred precision', err);
    }
  }

  private async loadTickers(symbols: string[]): Promise<void> {
    if (symbols.length === 0) return;
    try {
      const rows = await fetchTickers(symbols);
      for (const r of rows) {
        const t = this.get(r.symbol).ticker;
        // Only seed fields the socket has not already filled in — the stream
        // is fresher than a REST round trip by the time it lands.
        if (!Number.isFinite(t.last)) t.last = +r.lastPrice;
        t.open = +r.openPrice;
        t.high = +r.highPrice;
        t.low = +r.lowPrice;
        t.change = +r.priceChange;
        t.changePct = +r.priceChangePercent;
        t.volume = +r.volume;
        t.quoteVolume = +r.quoteVolume;
        t.tradeCount = r.count;
      }
      this.notify();
    } catch (err) {
      console.warn('[store] ticker seed failed', err);
    }
  }

  // -- mutations ------------------------------------------------------------

  addSymbol(symbol: string): void {
    const s = symbol.toUpperCase().trim();
    if (!s || this.symbols.includes(s)) {
      if (s) this.setActive(s);
      return;
    }
    this.symbols = [...this.symbols, s];
    this.attach(s);
    if (!DEMO) { void this.loadMeta([s]); void this.loadTickers([s]); }
    this.persist();
    this.emitStructure();
  }

  removeSymbol(symbol: string): void {
    if (this.symbols.length <= 1) return;
    this.symbols = this.symbols.filter((s) => s !== symbol);
    this.detach(symbol);
    if (this.active === symbol) {
      this.active = this.symbols[0];
      this.attachActive(this.active);
    }
    this.persist();
    this.emitStructure();
  }

  reorderSymbol(from: number, to: number): void {
    if (from === to || from < 0 || to < 0) return;
    if (from >= this.symbols.length || to >= this.symbols.length) return;
    const next = [...this.symbols];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    this.symbols = next;
    this.persist();
    this.emitStructure();
  }

  setActive(symbol: string): void {
    const s = symbol.toUpperCase();
    if (this.active === s) return;
    if (!this.symbols.includes(s)) {
      this.addSymbol(s);
    }
    this.active = s;
    this.attachActive(s);
    this.persist();
    this.emitStructure();
  }

  setInterval(interval: Interval): void {
    if (this.interval === interval) return;
    this.interval = interval;
    this.attachActive(this.active);
    this.persist();
    this.emitStructure();
  }

  // -- notification ---------------------------------------------------------

  /**
   * Coalesce the flood. Thousands of ingest calls a second collapse into one
   * React notification every UI_TICK_MS.
   */
  private notify(): void {
    if (this.tickPending) return;
    this.tickPending = true;
    this.tickTimer = window.setTimeout(() => {
      this.tickPending = false;
      this.version++;
      this.tick.emit();
    }, UI_TICK_MS);
  }

  private emitStructure(): void {
    this.structureVersion++;
    this.version++;
    this.structure.emit();
    this.tick.emit();
  }

  demoPaused = false;
  private demoTimer = 0;
  toggleDemoPause(): void { this.demoPaused = !this.demoPaused; this.emitStructure(); }

  private seedDemo(symbol: string): void {
    const st = this.get(symbol);
    for (const interval of new Set<Interval>(['1m', this.interval])) {
      const series=st.series(interval);
      if(!series.loaded) series.replaceAll(demoCandles(symbol,interval));
    }
    st.pricePrecision=demoBase(symbol)<1?5:2;
    if (!Number.isFinite(st.ticker.last)) {
      const series=st.series(this.interval), last=series.close.get(series.length-1);
      Object.assign(st.ticker,{last,open:last*.986,high:last*1.024,low:last*.972,volume:125000,quoteVolume:125000*last,tradeCount:82450});
    }
    this.advanceDemo(symbol);
  }

  private advanceDemo(symbol: string): void {
    const st=this.get(symbol), now=Date.now(), base=demoBase(symbol);
    const last=st.ticker.last*(1+Math.sin(now/1400+base)*.00008);
    this.onTrade(st,{e:'trade',E:now,s:symbol,t:now,p:String(last),q:String(680/base),T:now,m:Math.sin(now/350)<0});
    Object.assign(st.ticker,{change:last-st.ticker.open,changePct:(last/st.ticker.open-1)*100,bestBid:last-base*.00004,bestAsk:last+base*.00004});
    const levels=(sign:number):[string,string][]=>Array.from({length:100},(_,i)=>[
      (last+sign*base*.00004*(i+1)).toFixed(st.pricePrecision),
      ((.1+Math.abs(Math.sin(i*7+now/2000)))*68000/base).toFixed(5),
    ]);
    st.book.applySnapshot({lastUpdateId:now,bids:levels(-1),asks:levels(1)});
  }
}

/**
 * The alert engine registers here rather than the store importing it, so the
 * data layer stays free of any dependency on UI features.
 */
let alertHook: ((symbol: string, price: number) => void) | null = null;
export function setPriceHook(fn: ((symbol: string, price: number) => void) | null): void {
  alertHook = fn;
}

export const store = new MarketStore();
